import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { Field, FieldArray } from 'formik'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { IconAlertTriangle } from '@tabler/icons-react'
import { v4 as uuid } from 'uuid'
import pluralize from 'pluralize'

import { Divider } from '@/components/dividers/Divider'

import { Checkbox, NumberField, Select, TextArea, TextField } from '@/components/forms/formik'
import { Banner } from '@/components/banners/Banner'
import { PlaybookEntryFormTriggers } from '@/views/Playbooks/components/PlaybookEntryFormTriggers'
import { Button } from '@/components/buttons/Button'

import { InsufficientCallDataBanner } from '@/components/banners/InsufficientCallDataBanner'
import { AiToggle } from '@/components/ai/AiToggle'
import { PromptEditor } from '@/components/ai/PromptEditor'
import { PromptTester } from '@/components/ai/PromptTester'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { PlaybookFormActions } from '../../components/PlaybookFormActions'
import { FieldWarning } from '../../components/FieldWarning'
import { FormObserver } from '../../components/FormObserver'
import { accessors, checklistTriggerTypes } from '../../playbook.helpers'

import BulkEditToggle from '../shared/BulkEditToggle'
import { ChecklistDescriptionField } from './ChecklistDescriptionField'
import { isLlmChecklistEntry } from './checklist.helpers'

export const convertToSeconds = (values) => {
  const { trigger } = values

  if (trigger.occurrence_required) {
    const { trigger } = values

    // Gotta convert minutes to seconds for the backend
    // We store ints in the backend, so while 4.5min will multiple and save OK, 4.52343 will not
    const requiredBefore = Math.floor(
      trigger?.required_before_time_unit === 'minutes'
        ? trigger?.required_before * 60 || 0
        : trigger.required_before
    )
    return requiredBefore
  }
  return undefined
}

export const CheckboxWrap = ({ label, text, children, className }) => (
  <div className={classNames('flex-align-center flex-space-between gap mb', className)}>
    <div className="flex-column">
      <div className="label">{label}</div>
      <div>{text}</div>
    </div>
    {children}
  </div>
)

export const FormSection = ({ label, text, children }) => (
  <>
    <div className="flex-align-center flex-space-between gap mb">
      <div className="flex-column">
        <div className="label">{label}</div>
        <div>{text}</div>
      </div>
    </div>
    <div className="flex-align-center flex-space-between gap mb">{children}</div>
  </>
)

export const ChecklistFormComponent = ({
  isCreate,
  handleSubmit,
  handleCancel,
  errors,
  entryId,
  readOnly,
  flags,
  values,
  initialValues,
  ...formikProps
}) => {
  const dispatch = useDispatch()
  const {
    playbook: {
      cid,
      mostRecentId,
      language,
      dynamicChecklistEnabled,
      organization_id: playbookOrgId,
    },
    loading,
    listIDsInDB = {},
  } = useSelector((state) => state.playbook)
  const { massPlaybookUpdate: { isEnabled: massPlaybookUpdateEnabled } = {} } = useSelector(
    (state) => state.playbooks
  )
  const { org_realtime_coaching_access: hasRtcAccess, organizationid } = useSelector(
    (state) => state.currentUser
  )
  const { ai_checklist_enabled: currentOrgAiChecklistEnabled } = useSelector(
    (state) => state.currentOrganization
  )
  const isBaltoAdmin = organizationid === 1
  const organizations = useSelector((state) => state.organizations)
  const playbookOrganization = organizations?.find((org) => org.id === playbookOrgId)
  const aiChecklistEnabled = isBaltoAdmin
    ? playbookOrganization?.ai_checklist_enabled
    : currentOrgAiChecklistEnabled
  const [aiPromptEnabled, setAiPromptEnabled] = useState(!isEmpty(values.ai_checklist_prompt))
  const [aiPromptLoading, setAiPromptLoading] = useState(false)
  const [promptError, setPromptError] = useState({})
  const [committedPrompt, setCommittedPrompt] = useState(null)
  const isExistingItem = listIDsInDB.checklist.includes(entryId)
  const trimmedPrompt = values.ai_checklist_prompt?.trim() || ''
  const submitDisabled = readOnly || !isEmpty(errors)
  const showOutdatedPlaybookWarning = isCreate && readOnly
  const updatedTriggerTypeOptions =
    flags?.smartPhrasesTriggerType && language === 'english'
      ? [...checklistTriggerTypes, { label: 'Smart Phrases', value: 'smart_phrases' }]
      : checklistTriggerTypes

  const { visibility } = values
  const getBlankVisibilityRule = () => {
    return { key: '', value: '', operator: '==', id: uuid() }
  }
  const initializeVisibility = () => {
    if (!visibility) {
      formikProps.setFieldValue('visibility', {
        show_on_error: false,
        operator: 'AND',
        rules: [getBlankVisibilityRule()],
      })
    }
  }
  const clearVisibility = () => {
    formikProps.setFieldValue('visibility', undefined)
  }

  // We store everything in seconds. Let's start by converting it on load.
  useEffect(() => {
    const { trigger } = values

    if (trigger?.occurrence_required) {
      // Anything under 60 seconds will always be represented as seconds
      let timeValue = trigger.required_before
      let timeUnit = 'seconds'

      // When it's cleanly divided by 60 let's use minutes
      if (timeValue >= 60 && timeValue % 60 === 0) {
        timeValue /= 60
        timeUnit = 'minutes'
      } else if (timeValue >= 60) {
        // If it's not cleanly divisible, calculate minutes as a float to two decimal places
        const minutes = Math.floor(timeValue / 60)
        const seconds = timeValue % 60
        timeValue = minutes + seconds / 60
        timeUnit = 'minutes'
      }

      formikProps.setFieldValue('trigger.required_before', timeValue)
      formikProps.setFieldValue('trigger.required_before_time_unit', timeUnit)
    }
  }, [])

  // Auto-lock variations when ai_checklist_prompt has a value
  useEffect(() => {
    if (aiChecklistEnabled && trimmedPrompt) {
      formikProps.setFieldValue('is_locked', true)
    }
  }, [values.ai_checklist_prompt, aiChecklistEnabled])

  // Not used with checklist
  const updateTriggerWithAi = (krid) => {
    formikProps.setFieldValue('trigger.type', 'transcription_classifier')
    formikProps.setFieldValue('trigger.krid', krid)
  }

  // Not used with checklist
  const clearTriggerValues = ({ value }) => {
    formikProps.setFieldValue('trigger.phrases', undefined)
    formikProps.setFieldValue('trigger.krid', undefined)
    formikProps.setFieldValue('trigger.ratio', undefined)
    formikProps.setFieldValue('trigger.side', undefined)
    // A previous value is being validated after these changes if we don't update here
    formikProps.setFieldValue('trigger.type', value)
    formikProps.setFieldValue('trigger.required_before_time_unit', 'seconds')
  }

  const calculateTimeInSeconds = (timeValue, timeUnit) => {
    // We store everything in seconds on the backend
    if (timeUnit === 'minutes') {
      timeValue *= 60
    }

    return timeValue
  }
  const buildTimeLabel = (values) => {
    const { required_before: timeValue, required_before_time_unit: timeUnit } = values.trigger
    // Handle poor user input, or lack thereof
    if (timeValue === undefined || timeValue === '') {
      return 'Enter a number'
    }
    if (timeValue < 1) {
      return 'Please give the agent a moment to complete this item'
    }
    if (timeUnit === undefined || timeUnit === '') {
      return 'Select a unit of time'
    }

    const timeInSeconds = calculateTimeInSeconds(timeValue, timeUnit)
    const minutes = Math.floor(timeInSeconds / 60)
    // Because 1.5 minutes is a thing
    const seconds = Math.floor(timeInSeconds % 60)

    let humanReadableTime = 'This item will be required '

    if (minutes > 0) {
      humanReadableTime += `${pluralize('minute', Number(minutes), true)}`
    }
    if (minutes > 0 && seconds > 0) {
      humanReadableTime += ' and '
    }
    if (seconds > 0) {
      humanReadableTime += `${pluralize('second', Number(seconds), true)}`
    }

    humanReadableTime += ' after the start of the call'
    return humanReadableTime
  }

  const convertAndSubmit = (event) => {
    event.preventDefault()
    const requiredBefore = convertToSeconds(values)
    if (requiredBefore) {
      formikProps.setFieldValue('trigger.required_before', requiredBefore)
    }
    handleSubmit()
  }

  return (
    <div className="playbook-detail__content playbook-checklist-section__scrollable">
      <form className="field-stack" onSubmit={convertAndSubmit}>
        <FormObserver entryId={entryId} sectionName={accessors.CHECKLIST} />
        {showOutdatedPlaybookWarning && (
          <Banner warning icon={<IconAlertTriangle />} header="Outdated Playbook Version">
            The playbook version you are trying to edit is out of date. To add a checklist item,
            please use the <a href={`/playbooks/${mostRecentId}/checklist`}>most recent version</a>{' '}
            of this playbook.
          </Banner>
        )}
        {isExistingItem && massPlaybookUpdateEnabled && (
          <Field
            label="Current Value"
            value={initialValues.name}
            name="previous"
            component={TextArea}
            disabled
          />
        )}
        <Field
          required
          label={isExistingItem && massPlaybookUpdateEnabled ? 'New Value' : 'Checklist Item'}
          name="name"
          component={TextArea}
          disabled={readOnly}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
            }
          }}
        />
        {!readOnly && <FieldWarning accessor="checklist" field="name" entryId={entryId} />}

        {/* TODO: someone needs to make this look good, it looks poor, but allows me to test backend - Quinn */}
        {dynamicChecklistEnabled && (
          <>
            <h2>Conditional Visibility</h2>
            <div>
              {!visibility && !readOnly && (
                <Button onClick={initializeVisibility} className="mb">
                  Add visibility rule
                </Button>
              )}

              {visibility && (
                <>
                  <CheckboxWrap
                    label="Show on missing keys"
                    text="Whether to show if some keys in rules are missing from call metadata (otherwise will hide on any missing key)"
                  >
                    <Field
                      name="visibility.show_on_error"
                      component={Checkbox}
                      toggle
                      inline
                      reverse
                      disabled={readOnly}
                    />
                  </CheckboxWrap>

                  <FormSection
                    label="Rules"
                    text="Conditional rules (NOTE: not case or type sensitive)"
                  />

                  <FieldArray name="visibility.rules">
                    {() => (
                      <>
                        {visibility.rules.map((rule, index) => (
                          <div
                            key={rule.id}
                            style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}
                          >
                            <Field
                              name={`visibility.rules[${index}].key`}
                              component={TextField}
                              placeholder="Key"
                              disabled={readOnly}
                              required
                            />
                            <Field
                              name={`visibility.rules[${index}].operator`}
                              component={Select}
                              options={[
                                { label: '=', value: '==' },
                                { label: '!=', value: '!=' },
                              ]}
                              disabled={readOnly}
                              required
                            />
                            <Field
                              name={`visibility.rules[${index}].value`}
                              component={TextField}
                              placeholder="Value"
                              disabled={readOnly}
                              required
                            />
                            {!readOnly && (
                              <Button
                                critical
                                onClick={() => {
                                  const newRules = [...visibility.rules]
                                  newRules.splice(index, 1)
                                  if (newRules.length === 0) {
                                    clearVisibility()
                                  } else {
                                    formikProps.setFieldValue('visibility.rules', newRules)
                                  }
                                }}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        ))}

                        {!readOnly && (
                          <Button
                            onClick={() =>
                              formikProps.setFieldValue('visibility.rules', [
                                ...(visibility.rules || []),
                                getBlankVisibilityRule(),
                              ])
                            }
                            className="mb"
                          >
                            Add rule
                          </Button>
                        )}

                        {visibility.rules.length > 1 && (
                          <FormSection
                            label="Rule operator"
                            text="Whether to use AND or OR logic to combine rules"
                          >
                            <Field
                              name="visibility.operator"
                              component={Select}
                              options={[
                                { label: 'AND', value: 'AND' },
                                { label: 'OR', value: 'OR' },
                              ]}
                              disabled={readOnly}
                            />
                          </FormSection>
                        )}
                      </>
                    )}
                  </FieldArray>
                </>
              )}
            </div>
          </>
        )}

        <h2>Checklist Item Configuration</h2>
        {aiChecklistEnabled && (
          <AiToggle
            name="aiPromptEnabled"
            label="AI Checklist Trigger"
            description="Use an LLM to automatically check off this item when the agent says or does the right thing during a call."
            disabled={readOnly || aiPromptLoading}
            checked={aiPromptEnabled}
            onChange={() => {
              const newValue = !aiPromptEnabled
              setAiPromptEnabled(newValue)
              setPromptError({})
              if (!newValue) {
                formikProps.setFieldValue('ai_checklist_prompt', '')
              }
            }}
          >
            <PromptEditor
              name="ai_checklist_prompt"
              value={values.ai_checklist_prompt}
              generateContent={
                values.name?.trim() ? { context: `${accessors.CHECKLIST} - ${values.name}` } : null
              }
              section={accessors.CHECKLIST}
              readOnly={readOnly}
              setFieldValue={formikProps.setFieldValue}
              onLoadingChange={setAiPromptLoading}
              onInsufficientData={() => setPromptError({ insufficientData: true })}
              onFocus={() => setPromptError({})}
              onPromptCommitted={setCommittedPrompt}
            />
            {promptError.insufficientData && (
              <InsufficientCallDataBanner organizationId={playbookOrgId} playbookCid={cid} />
            )}
            {isLlmChecklistEntry(values) && (
              <>
                <ChecklistDescriptionField committedPrompt={committedPrompt} />
                {!readOnly && (
                  <FieldWarning accessor="checklist" field="description" entryId={entryId} />
                )}
              </>
            )}
            <PromptTester prompt={values.ai_checklist_prompt} />
          </AiToggle>
        )}
        {hasRtcAccess && (
          <div className="playbook-config-container">
            <CheckboxWrap
              label="Add Time Requirement"
              text="Remind agents to say this item within a specified time (e.g., a few seconds or minutes) after the call starts. This feature is ideal for ensuring compliance with mandatory scripts."
            >
              <Field
                name="trigger.occurrence_required"
                component={Checkbox}
                toggle
                inline
                reverse
                disabled={readOnly}
              />
            </CheckboxWrap>
            {values.trigger?.occurrence_required && (
              <>
                <div className="time-requirement">
                  <Field
                    name="trigger.required_before"
                    component={NumberField}
                    onChange={(e) =>
                      formikProps.setFieldValue(
                        'trigger.required_before',
                        calculateTimeInSeconds(e.target.value)
                      )
                    }
                  />
                  <Field
                    required
                    name="trigger.required_before_time_unit"
                    component={Select}
                    options={[
                      { label: 'Minutes', value: 'minutes' },
                      { label: 'Seconds', value: 'seconds' },
                    ]}
                    placeholder="Unit of Time"
                  />
                </div>
                <ErrorMessage content={buildTimeLabel(values)} />
              </>
            )}
          </div>
        )}
        <CheckboxWrap
          className="playbook-config-container"
          label="Prevent Variants"
          text="Restrict agents from creating variations or modifying this item. We recommend keeping this option off unless required for compliance."
        >
          <Field
            name="is_locked"
            component={Checkbox}
            toggle
            inline
            reverse
            disabled={readOnly || !!trimmedPrompt}
          />
        </CheckboxWrap>

        {flags?.playbookChecklistTriggerTypeEnabled && (
          <>
            <Divider>Trigger</Divider>
            <Field
              required
              label="Trigger Type"
              name="trigger.type"
              component={Select}
              options={updatedTriggerTypeOptions}
              isClearable={false}
              disabled={readOnly}
              onChange={clearTriggerValues}
            />
            <PlaybookEntryFormTriggers
              loading={loading}
              values={values}
              dispatch={dispatch}
              triggerType={values.trigger?.type}
              formikProps={formikProps}
              readOnly={readOnly}
              sideHidden
              triggerHidden
              accessor={accessors.CHECKLIST}
              entryId={entryId}
              updateTriggerWithAi={updateTriggerWithAi}
            />
          </>
        )}
        <BulkEditToggle
          accessor={accessors.CHECKLIST}
          readOnly={readOnly}
          isUpdate={isExistingItem}
        />
        <PlaybookFormActions
          isDisabled={submitDisabled}
          handleCancel={handleCancel}
          dirty={formikProps.dirty}
          values={values}
        />
      </form>
    </div>
  )
}

export const ChecklistForm = withLDConsumer()(ChecklistFormComponent)
